import { FC, ReactElement, useMemo, useState } from "react";
import {
  Box,
  // Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
// import { ExpandMore } from "@mui/icons-material";

import {
  DutiesCard,
  ImportCard,
  // AdvanceFilter,
  MerticsCard,
  // DutiesUtilizedCard,
  TariffCard,
  ImportExportCard,
  VendorsCard,
} from "./components";
import { Filters, initialFilters } from "./components/advance-filter";
import { DateRangeFilter, PlaceholderCard } from "@/components";
import { useAppSelector } from "@/store";
import {
  coins_dollar,
  distinct_hts,
  distinct_imports,
  duties_placeholder,
  // filter_icon,
  imports_exports_placeholder,
  imports_placeholder,
  metrics_placeholder,
  percent_circle,
  tariff_code_placeholder,
} from "@/constants";
import {
  useGetDashboardDutySavingUtilizedQuery,
  useGetDashboardDutySpendQuery,
  useGetDashboardImportsExportsQuery,
  useGetDashboardImportsQuery,
  useGetDashboardManufacturersQuery,
  useGetDashboardMetricsQuery,
  useGetDashboardTariffHtsQuery,
  useGetDashboardUpdateDateQuery,
} from "@/services";
import { Range } from "react-date-range";
import { format, startOfYear, differenceInMonths } from "date-fns";
import { roles } from "@/roles";
import {
  formatDateOrTime,
  formatNumberWithSuffix,
  numberWithCommas,
  responsiveFontSizes,
  spacing,
} from "@/utils";

const TARIFF_CODE_PAGE_SIZE = 1000; // Fetch all codes at once

const Analytics: FC = (): ReactElement => {
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant,areTenantsLoading } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  const [dateFilter, setDateFilter] = useState<
    Pick<Range, "startDate" | "endDate">
  >({
    startDate: startOfYear(new Date()),
    endDate: new Date(),
  });

  // const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
  //   null
  // );
  const [filters, _setFilters] = useState<Filters>({ ...initialFilters });
  
  const useMonthlyAggregation = useMemo(() => {
    const start = dateFilter.startDate ?? new Date();
    const end = dateFilter.endDate ?? new Date();
    return differenceInMonths(end, start) < 36; // under 3 years → use monthly/quarter aggregation
  }, [dateFilter]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  // const appliedFilterLength = useMemo(() => {
  //   return Object.values(filters).filter(Boolean).length;
  // }, [filters]);

  // const openFilterModal = (event: MouseEvent<HTMLButtonElement>) => {
  //   setAnchorElement(event.currentTarget);
  // };

  const commonFilters = useMemo(() => {
    return {
      dateFrom: format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
      dateTo: format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
      clientId: selectedClient?._id,
      ...(user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT" && {
        tenantId: selectedTenant?._id,
      }),
      ...(filters.dutyType && { attribute: filters.dutyType }),
    };
  }, [dateFilter, selectedClient, filters.dutyType, selectedTenant]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const commonOptions = useMemo(() => {
    // If user is TDADMIN and selected tenant type is TRADEDIVE_CLIENT,
    // only skip if there's no selected tenant
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    // Default behavior - skip if client selection is required but not selected
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant, user]);

  const { data: lastUpdated, isFetching: isFetchingDate } =
    useGetDashboardUpdateDateQuery(
      {
        params: {
          clientId: commonFilters.clientId,
          ...(isTDAdminTCD && {
            tenantId: selectedTenant?._id,
          }),
        },
      },
      commonOptions
    );

  const {
    data: importsData,
    isLoading: isImportsLoading,
    isFetching: isImportsFetching,
  } = useGetDashboardImportsQuery(
    {
      dashboardVisibility: {
        ...commonFilters,
        groupBy: useMonthlyAggregation ? "month" : "year",
      },
    },
    { ...commonOptions }
  );

  const { data: _dutySavingData, isFetching: isFetchingDutySaving } =
    useGetDashboardDutySavingUtilizedQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions }
    );

  const { data: manufacturersData, isFetching: isManufacturersFetching} =
    useGetDashboardManufacturersQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions }
    );

  const {
    data: tariffData,
    isLoading: isTariffLoading,
    isFetching: isTariffFetcing,
  } = useGetDashboardTariffHtsQuery(
    {
      dashboardVisibility: {
        ...commonFilters,
        minThreshold: 0,
        pageNo: 1,
        pageSize: TARIFF_CODE_PAGE_SIZE,
      },
    },
    { ...commonOptions }
  );

  const { data: dutySpendData, isFetching: isDutySpendFetching } =
    useGetDashboardDutySpendQuery(
      {
        dashboardVisibility: {
          dateFrom: commonFilters.dateFrom,
          dateTo: commonFilters.dateTo,
          clientId: selectedClient?._id,
          ...(isTDAdminTCD && {
            tenantId: selectedTenant?._id,
          }),
        },
      },
      { ...commonOptions }
    );

  const { data: metricsData, isFetching: isMetricsFetching } =
    useGetDashboardMetricsQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions }
    );

  const { data: importExportData, isFetching: isImportExportFetching } =
    useGetDashboardImportsExportsQuery(
      {
        dashboardVisibility: {
          ...commonFilters,
        },
      },
      { ...commonOptions }
    );

  const { totalMfnDuty, total201Duty, total232Duty, total301Duty, others, totalDuty, totalDutiesPaid } =
    useMemo(() => {
      const {
        totalMfnDuty = 0,
        total201Duty = 0,
        total232Duty = 0,
        total301Duty = 0,
        totalLineTariffGoodValueAmount = 0,
        dutySum = 0,
        totalDutiesPaid = 0,
        totalOtherDuties = 0
      } = dutySpendData?.data ?? {};
      return {
        totalMfnDuty,
        total201Duty,
        total232Duty,
        total301Duty,
        totalLineTariffGoodValueAmount,
        others: totalOtherDuties,
        totalDuty: dutySum,
        totalDutiesPaid,
      };
    }, [dutySpendData]);

  const vendors: IVendor[] = useMemo(() => {
    if (!manufacturersData?.data) return [];

    return manufacturersData.data.map(
      ({
        _id = "",
        manufacturer = "",
        manufacturerName = "",
        customValue = 0,
        tariffDuty = 0,
        tariffValue = 0,
      }): IVendor => {
        return {
          _id,
          manufacturer,
          manufacturerName,
          customValue,
          tariffDuty,
          tariffValue,
        };
      }
    );
  }, [manufacturersData]);

  const {
    avgShipmentValue,
    distinctImports,
    effectiveDutyRates,
    distinctHts,
    customsValue,
    customsDuty,
    distinctSuppliers,
    dutySaved,
    aceRange,
  } = useMemo(() => {
    const {
      avgShipmentValue = 0,
      distinctImports = 0,
      effectiveDutyRates = 0,
      distinctHts = 0,
      customsValue = 0,
      customsDuty = 0,
      distinctSuppliers = 0,
      totalDutySaving = 0,
      aceRange,
    } = metricsData?.data ?? {};

    return {
      avgShipmentValue:  numberWithCommas(avgShipmentValue),
      distinctImports: formatNumberWithSuffix(distinctImports),
      effectiveDutyRates: Number(effectiveDutyRates).toFixed(2) + "%",
      distinctHts: formatNumberWithSuffix(distinctHts),
      customsValue: numberWithCommas(customsValue),
      customsDuty: numberWithCommas(customsDuty),
      distinctSuppliers,
      dutySaved: Math.round(totalDutySaving),
      aceRange,
    };
  }, [metricsData]);

  const isAnyApiLoading = useMemo(() => {
    return [
      isFetchingDate,
      isFetchingDutySaving,
      isManufacturersFetching,
      isTariffLoading,
      isImportsLoading,
      isDutySpendFetching,
      isMetricsFetching,
      isImportExportFetching,
    ].some(Boolean);
  }, [
    isFetchingDate,
    isFetchingDutySaving,
    isManufacturersFetching,
    isTariffLoading,
    isImportsLoading,
    isDutySpendFetching,
    isMetricsFetching,
    isImportExportFetching,
  ]);

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={spacing.md}
        gap={{ xs: 1.5, md: 0 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: responsiveFontSizes['lg'],
              fontWeight: 600,
              color: "neutral.1000",
            }}
          >
            Import Overview
          </Typography>
          {lastUpdated && (
            <Typography
              variant="body2"
              sx={{
                fontSize: responsiveFontSizes.sm,
                fontWeight: 400,
                color: "neutral.500",
                mt: spacing.xs,
              }}
            >
              ACE data refreshed on{" "}
              {formatDateOrTime(lastUpdated, "both")}
            </Typography>
          )}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={{ xs: 1, md: 2 }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
            <DateRangeFilter value={dateFilter} onSelectDate={setDateFilter} />
            {/* <Button
              variant="primary-contained"
              onClick={openFilterModal}
              id="advance-filter-anchor"
              aria-controls={anchorElement ? "advance-filter-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={anchorElement ? "true" : undefined}
              sx={{
                minWidth: 230,
                flex: {
                  xs: 1,
                  sm: "initial",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <img src={filter_icon} alt="filter-icon" />
                <Typography variant="body1" color="white" fontWeight={500}>
                  Advance Filter
                </Typography>
                {appliedFilterLength > 0 && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      background: "#FFFFFF26",
                      display: "grid",
                      placeContent: "center",
                      borderRadius: "50%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="white"
                      fontWeight={500}
                    >
                      {appliedFilterLength}
                    </Typography>
                  </Box>
                )}
                <ExpandMore
                  sx={{
                    color: "white",
                    fontSize: 18,
                  }}
                />
              </Stack>
            </Button>
            <AdvanceFilter
              anchorEl={anchorElement}
              appliedFilters={filters}
              onFiltersChange={setFilters}
              onClose={() => setAnchorElement(null)}
            /> */}
        </Stack>
      </Stack>
      {isAnyApiLoading || areClientsLoading || areTenantsLoading ? (
        <Stack flex={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2.25} mb={2.5} data-testid="analytics-grid">
          <Grid item xs={12} md={4} lg={4}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <ImportCard
                data={importsData?.data ?? []}
                xAxisMode={useMonthlyAggregation ? "quarter" : "year"}
                isLoading={isImportsFetching}
              />
            ) : (
              <PlaceholderCard
                title="Overall Import Value"
                description="Track and analyze the total value of all imports over time"
                imgUrl={imports_placeholder}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <DutiesCard
                data={[
                  { name: "MFN", value: totalMfnDuty },
                  { name: "Section 201", value: total201Duty },
                  { name: "Section 232", value: total232Duty },
                  { name: "301", value: total301Duty },
                  {
                    name: "Trump Duties",
                    value: others,
                  },
                ].filter(item => item.value > 0)}
                total={totalDuty}
                totalDutiesPaid={totalDutiesPaid}
              />
            ) : (
              <PlaceholderCard
                title="Overall Duties Paid"
                description="Monitor total customs duties paid on imports, with detailed breakdowns by category and automatic duty calculations."
                imgUrl={duties_placeholder}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <MerticsCard
                data={[
                  {
                    title: "Avg. Shipment Value",
                    value: "$" + avgShipmentValue,
                    icon: coins_dollar,
                  },
                  {
                    title: "Distinct Imports",
                    value: distinctImports,
                    icon: distinct_imports,
                  },
                  {
                    title: "Effective Duty Rate",
                    value: effectiveDutyRates,
                    icon: percent_circle,
                  },
                  {
                    title: "Distinct HTS",
                    value: distinctHts,
                    icon: distinct_hts,
                  },
                ]}
                aceRange={aceRange}
              />
            ) : (
              <PlaceholderCard
                title="Import Metrics"
                description="Access key performance indicators including volume trends, average shipment values, and compliance metrics for your import operations."
                imgUrl={metrics_placeholder}
              />
            )}
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <DutiesUtilizedCard data={dutySavingData?.data ?? []} />
          </Grid> */}
          <Grid item xs={12}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <ImportExportCard
                data={importExportData?.data || []}
                footerData={{
                  avgShipmentValue,
                  customsDuty,
                  customsValue,
                  distinctImports,
                  distinctSuppliers,
                  dutySaved,
                  effectiveDutyRates,
                }}
              />
            ) : (
              <PlaceholderCard
                title="Imports by Country of Origin"
                description="Analyze import values and duty rates by country of origin to identify cost-saving opportunities and optimize your supply chain."
                imgUrl={imports_exports_placeholder}
                centerContent
                cardStyles={{
                  minHeight: "660px",
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={6} xl={6}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <TariffCard
                data={tariffData?.data ?? []}
                isLoading={isTariffFetcing}
              />
            ) : (
              <PlaceholderCard
                title="Tariff Codes"
                description="Visualize your top HTS codes by import value and duty impact, helping identify which classifications are driving your customs costs and trade flows"
                imgUrl={tariff_code_placeholder}
                centerContent
                cardStyles={{
                  minHeight: "410px",
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={6} xl={6}>
            {!canSelectClients || selectedClient || isTDAdminTCD ? (
              <VendorsCard data={vendors} referenceRowCount={(tariffData?.data ?? []).length} />
            ) : (
              <PlaceholderCard
                title="Vendors"
                description="Track and analyze vendor performance and duty impacts, helping identify which suppliers are driving your customs costs and trade flows"
                imgUrl={imports_exports_placeholder}
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Analytics;
